import { SHORTENED_LABELS } from '@configs/language/statesTranslations';
import { StatusComponent, Tooltip } from '@efexteam/components';
import { capitalizeFirst } from '@utils/formater';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

type States =
  | 'Add'
  | 'Bitso'
  | 'Completed'
  | 'Canceled'
  | 'Created'
  | 'Delivered'
  | 'Deposited'
  | 'Efex'
  | 'Missing'
  | 'Not_processed'
  | 'Pending_to_deposit'
  | 'Pending_to_process_scheduled_payment'
  | 'Waiting_for_due_date'
  | 'Validation_amount'
  | 'Pending_to_process'
  | 'Pending'
  | 'Processing'
  | 'Rejected'
  | 'Expired'
  | 'Remove'
  | 'Waiting_for_deposit'
  | 'Wallet_conversion'
  | 'Wallet_payment'
  | 'Wallet_transfer'
  | 'Withdrawal'
  | 'Deposit_failed'
  | 'Balance_discounted'
  | 'Refunded'
  | 'New_payment_created'
  | 'Swift'
  | 'Lending'
  | 'Bitso_deposit_error'
  | 'Provider_processing'
  | 'Pending_to_approve_by_user'
  | 'Active'
  | 'Inactive'
  | 'Declined'
  | 'Approval_time_expired';

export const WAITING_FOR_DUE_DATE = 'Waiting_for_due_date';

const getStatusVariant = (
  status: States
): 'positive' | 'warning' | 'negative' | 'info' | 'special' => {
  switch (status) {
    case 'Completed':
    case 'Delivered':
    case 'Deposited':
    case 'Add':
    case 'Bitso':
    case 'Created':
    case 'Remove':
    case 'Waiting_for_deposit':
    case 'Wallet_conversion':
    case 'Wallet_payment':
    case 'Wallet_transfer':
    case 'Balance_discounted':
    case 'Swift':
    case 'Lending':
    case 'New_payment_created':
    case 'Active':
      return 'positive';
    case 'Pending_to_deposit':
    case 'Waiting_for_due_date':
    case 'Validation_amount':
    case 'Pending_to_process':
    case 'Pending':
    case 'Processing':
      return 'warning';
    case 'Canceled':
    case 'Efex':
    case 'Missing':
    case 'Not_processed':
    case 'Rejected':
    case 'Expired':
    case 'Withdrawal':
    case 'Deposit_failed':
    case 'Refunded':
    case 'Bitso_deposit_error':
    case 'Inactive':
    case 'Declined':
    case 'Approval_time_expired':
      return 'negative';
    case 'Pending_to_approve_by_user':
    case 'Pending_to_process_scheduled_payment':
    case 'Provider_processing':
      return 'special';
    default:
      return 'info';
  }
};

interface StateSpanProps {
  status: string;
}

export const StateSpan = ({ status }: StateSpanProps) => {
  const { t, i18n } = useTranslation();
  const stateValue = capitalizeFirst(status) as States;

  const getTranslation = (state: States): string => {
    const translationKeys: Record<States, string> = {
      Add: 'stateAdd',
      Bitso: 'stateAdd',
      Completed: 'stateCompleted',
      Canceled: 'stateCanceled',
      Created: 'stateCompleted',
      Delivered: 'stateDelivered',
      Deposited: 'stateDeposited',
      Efex: 'stateRemove',
      Missing: 'stateMissing',
      Not_processed: 'stateNotProcessed',
      Pending_to_deposit: 'statePendingDeposit',
      Pending_to_process_scheduled_payment: 'stateForwardPending',
      Waiting_for_due_date: 'stateWaitingForDueDate',
      Validation_amount: 'stateForwardPending',
      Pending_to_process: 'statePendingToProcess',
      Pending: 'statePending',
      Processing: 'stateProcessing',
      Rejected: 'stateRejected',
      Expired: 'stateExpired',
      Remove: 'stateRemove',
      Waiting_for_deposit: 'stateWaiting',
      Wallet_conversion: 'stateWalletConversion',
      Wallet_payment: 'stateWallet',
      Wallet_transfer: 'stateWalletConversion',
      Withdrawal: 'stateRemove',
      Deposit_failed: 'depositFailed',
      Balance_discounted: 'balanceDiscounted',
      Refunded: 'stateRefunded',
      New_payment_created: 'stateNewPaymentCreated',
      Swift: 'stateSwift',
      Lending: 'stateLending',
      Bitso_deposit_error: 'stateBitsoDepositError',
      Provider_processing: 'stateProviderProcessing',
      Pending_to_approve_by_user: 'statePendingToApproveByUser',
      Active: 'stateActive',
      Inactive: 'stateInactive',
      Declined: 'stateDeclined',
      Approval_time_expired: 'stateApprovalTimeExpired',
    };

    return t(translationKeys[state]);
  };

  const getShortenedLabel = (state: States, fullLabel: string): string => {
    const currentLanguage = i18n.language.toUpperCase() as 'EN' | 'ES';
    const shortenedLabels = SHORTENED_LABELS[currentLanguage];

    const shortenedKeyMap: Record<States, string | undefined> = {
      Pending_to_process_scheduled_payment: 'shortForwardPending',
      Waiting_for_due_date: 'shortWaitingForDueDate',
      Waiting_for_deposit: 'shortWaiting',
      Pending_to_deposit: 'shortPendingDeposit',
      Not_processed: 'shortNotProcessed',
      New_payment_created: 'shortNewPaymentCreated',
      Balance_discounted: 'shortBalanceDiscounted',
      Provider_processing: 'shortProviderProcessing',
      Pending_to_approve_by_user: 'shortPendingToApproveByUser',
    } as Record<States, string | undefined>;

    const shortenedKey = shortenedKeyMap[state];

    if (shortenedKey && shortenedLabels[shortenedKey]) {
      return shortenedLabels[shortenedKey];
    }

    return fullLabel;
  };

  const fullLabel = getTranslation(stateValue);
  const displayLabel = getShortenedLabel(stateValue, fullLabel);
  const needsTooltip = fullLabel !== displayLabel;

  const statusComponent = (
    <StatusComponent
      displayVariant="chip"
      size="small"
      status={getStatusVariant(stateValue)}
      label={displayLabel}
      aria-label={fullLabel}
    />
  );

  if (needsTooltip) {
    return (
      <Tooltip title={fullLabel} placement="top">
        <Box className="w-full">{statusComponent}</Box>
      </Tooltip>
    );
  }

  return statusComponent;
};
